import { ChevronLeft, Mail, Phone, Plus, X } from "lucide-react";
import {
  ContractDetails,
  PartnershipTermsDialog,
} from "@/components/Partnership/PartnershipTermsDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import {
  formatDate,
  getStatusColor,
  getStatusDisplayText,
} from "@/utils/format";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import ApplicationVideoLink from "@/components/Partnership/ApplicationVideoLink";
import { Button } from "@/components/ui/button";
import { ConvexError } from "convex/values";
import CreatorSocialCard from "@/components/Creators/CreatorSocialCard";
import { Id } from "@cvx/_generated/dataModel";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { TEAM_USER_IDS } from "@/constants/userIds";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@cvx/_generated/api";
import { cn } from "@/utils/misc";
import { useAccount } from "@/state/useAccount";
import { useToast } from "@/components/ui/use-toast";

export const Route = createFileRoute("/_app/_auth/creators/$creatorId/")({
  component: CreatorProfile,
  beforeLoad: async ({ context, params }) => {
    const creator = await context.queryClient.ensureQueryData(
      convexQuery(api.creators.getCreatorByCreatorId, {
        creatorId: params.creatorId as Id<"creators">,
      }),
    );
    return { creator };
  },
});

function CreatorProfile() {
  const { creatorId } = Route.useParams();
  const { appId } = useAccount();
  const { creator: routeCreator } = Route.useRouteContext();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch creator data using query hook so it updates when invalidated
  const { data: creator } = useQuery({
    ...convexQuery(api.creators.getCreatorByCreatorId, {
      creatorId: creatorId as Id<"creators">,
    }),
    // Use route creator as initial data
    initialData: routeCreator,
  });

  // Get current user to check if they're a team member
  const { data: currentUser } = useQuery({
    ...convexQuery(api.app.getCurrentUser, {}),
  });

  // Check if current user is a team member
  const isCreatorGridTeamMember = currentUser
    ? TEAM_USER_IDS.includes(currentUser._id)
    : false;

  const { mutateAsync: rejectApplication } = useMutation({
    mutationFn: useConvexMutation(api.applications.rejectApplication),
  });
  const { mutateAsync: createPartnership, isPending: isCreatingPartnership } =
    useMutation({
      mutationFn: useConvexMutation(api.partnerships.createUpdatePartnership),
    });
  const { mutateAsync: updateUGCAcceleratorMember, isPending: isUpdatingUGC } =
    useMutation({
      mutationFn: useConvexMutation(
        api.creators.updateUserIsUGCAcceleratorMember,
      ),
    });
  const { mutateAsync: updateIsCreatorPartner, isPending: isUpdatingPartner } =
    useMutation({
      mutationFn: useConvexMutation(api.creators.updateIsCreatorPartner),
    });
  const { mutateAsync: updateIsCiR, isPending: isUpdatingCiR } = useMutation({
    mutationFn: useConvexMutation(api.creators.updateIsCiR),
  });
  const {
    mutateAsync: updateShowLeaderboardOnHomeScreen,
    isPending: isUpdatingLeaderboard,
  } = useMutation({
    mutationFn: useConvexMutation(
      api.creators.updateShowLeaderboardOnHomeScreen,
    ),
  });
  const { mutateAsync: createCustomLeaderboard } = useMutation({
    mutationFn: useConvexMutation(api.creators.createCustomLeaderboard),
  });
  const { mutateAsync: addCreatorToCustomLeaderboard } = useMutation({
    mutationFn: useConvexMutation(api.creators.addCreatorToCustomLeaderboard),
  });
  const { mutateAsync: removeCreatorFromCustomLeaderboard } = useMutation({
    mutationFn: useConvexMutation(
      api.creators.removeCreatorFromCustomLeaderboard,
    ),
  });

  // Check if creator is in a custom leaderboard
  const { data: customLeaderboard } = useQuery({
    ...convexQuery(api.creators.getCreatorCustomLeaderboard, {
      creatorId: creatorId as Id<"creators">,
    }),
  });

  // Get all custom leaderboards (for superadmin dropdown)
  const { data: allCustomLeaderboards } = useQuery({
    ...convexQuery(api.creators.getAllCustomLeaderboards, {}),
    enabled: isCreatorGridTeamMember,
  });

  const [showCreateLeaderboardDialog, setShowCreateLeaderboardDialog] =
    useState(false);
  const [newLeaderboardName, setNewLeaderboardName] = useState("");
  const [showContractForm, setShowContractForm] = useState(false);
  const [contractDetails, setContractDetails] = useState<ContractDetails>({
    startDate: Date.now(),
    endDate: new Date(
      new Date().getFullYear(),
      new Date().getMonth() + 1,
      0,
    ).getTime(), // End of current month
    currency: "usd",
    requiredVideos: {
      count: 1,
      duration: 30, // 30 days default
    },
  });
  const [showRejectionDialog, setShowRejectionDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [selectedApplicationId, setSelectedApplicationId] =
    useState<Id<"creatorApplications"> | null>(null);
  const [selectedCampaignId, setSelectedCampaignId] =
    useState<Id<"campaigns"> | null>(null);
  const [showCampaignSelector, setShowCampaignSelector] = useState(false);

  // Check if this specific creator has an active contract for this app
  const { data: creatorContractsAndApplications } = useQuery(
    convexQuery(api.partnerships.getCreatorPartnershipsAndApplicationsWithApp, {
      creatorId: creatorId as Id<"creators">,
      appId: appId as Id<"apps">,
    }),
  );

  // Get selected campaign from the applications/partnerships data
  const selectedCampaign = selectedCampaignId
    ? creatorContractsAndApplications?.creatorApplications?.find(
        (app) => app.campaignId === selectedCampaignId,
      )?.campaign ||
      creatorContractsAndApplications?.creatorPartnerships?.find(
        (partnership) => partnership.campaignId === selectedCampaignId,
      )?.campaign ||
      creatorContractsAndApplications?.availableCampaigns?.find(
        (campaign) => campaign._id === selectedCampaignId,
      )
    : null;

  const isAbleToInviteCreator = useMemo(() => {
    return (
      appId &&
      !creatorContractsAndApplications?.creatorApplications.length &&
      !creatorContractsAndApplications?.creatorPartnerships.length
    );
  }, [appId, creatorContractsAndApplications]);

  // Determine display order based on relevance
  const { shouldShowPartnershipsFirst } = useMemo(() => {
    const partnerships =
      creatorContractsAndApplications?.creatorPartnerships || [];
    const applications =
      creatorContractsAndApplications?.creatorApplications || [];

    // Check if there's an active partnership (assuming active means not rejected/terminated)
    const hasActive = partnerships.some(
      (p) =>
        p.status && !["rejected", "terminated", "expired"].includes(p.status),
    );

    // Check if there's a pending application
    const hasPending = applications.some((app) => app.status === "pending");

    // Show partnerships first if there's an active partnership
    // Otherwise show applications first only if there's a pending application
    const shouldShowPartnershipsFirst = hasActive || !hasPending;

    return {
      shouldShowPartnershipsFirst,
      hasActivePartnership: hasActive,
      hasPendingApplication: hasPending,
    };
  }, [creatorContractsAndApplications]);

  if (!creator) {
    return <div>Creator not found</div>;
  }

  const handleApplicationAction = async (
    action: "approve" | "reject",
    applicationId: Id<"creatorApplications">,
    campaignId: Id<"campaigns">,
  ) => {
    if (action === "approve") {
      setSelectedApplicationId(applicationId);
      setSelectedCampaignId(campaignId);
      setShowContractForm(true);
    } else {
      setSelectedApplicationId(applicationId);
      setSelectedCampaignId(campaignId);
      setShowRejectionDialog(true);
    }
  };

  const handleRejectConfirm = async () => {
    if (!selectedApplicationId || !rejectionReason.trim()) return;

    try {
      await rejectApplication({
        applicationId: selectedApplicationId as Id<"creatorApplications">,
        rejectionReason: rejectionReason.trim(),
      });
      setShowRejectionDialog(false);
      setRejectionReason("");
      setSelectedApplicationId(null);
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof ConvexError
            ? error.data
            : "Failed to reject application",
        variant: "destructive",
      });
    }
  };

  const handleContractSubmit = async () => {
    if (!selectedApplicationId && !isAbleToInviteCreator) {
      toast({
        title: "Error",
        description: "No application selected",
        variant: "destructive",
      });
      return;
    }

    try {
      await createPartnership({
        creatorId: creatorId as Id<"creators">,
        campaignId: selectedCampaignId as Id<"campaigns">,
        appId: appId as Id<"apps">,
        creatorApplicationId:
          (selectedApplicationId as Id<"creatorApplications">) ?? undefined,
        startDate: new Date(contractDetails.startDate).getTime(),
        endDate: new Date(contractDetails.endDate).getTime(),
        currency: contractDetails.currency,
        requiredVideos: contractDetails.requiredVideos,
        flatFee: contractDetails.flatFee,
        cpmRate: contractDetails.cpmRate
          ? {
              ...contractDetails.cpmRate,
              cutoffPeriod: contractDetails.cpmRate.cutoffPeriod,
            }
          : undefined,
      });

      toast({
        title: "Success",
        description: "Partnership proposal sent successfully",
      });

      setShowContractForm(false);
      setSelectedApplicationId(null);
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to send partnership proposal",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="h-screen w-full overflow-y-auto bg-secondary px-6 py-8 dark:bg-black [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">
      <div className="mx-auto flex w-full max-w-screen-xl flex-col gap-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          className="flex w-fit items-center gap-2 text-primary/60 hover:text-primary"
          onClick={() => window.history.back()}
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </Button>

        {/* Profile Header */}
        <div className="flex flex-col rounded-lg border border-border bg-card">
          <div className="flex flex-row items-start justify-between p-6">
            {/* Left Column - Image and Info */}
            <div className="flex flex-row gap-6 flex-1 min-w-0 pr-4">
              <img
                src={creator.profileImage}
                alt={creator.name}
                className="h-24 w-24 rounded-full shrink-0 border-2 border-border"
              />

              <div className="flex-1 min-w-0">
                <div className="flex flex-col gap-2">
                  <h1 className="text-2xl font-bold text-primary truncate">
                    {creator.name}
                  </h1>
                  <p className="text-sm text-primary/60 line-clamp-2">
                    {creator.bio}
                  </p>
                </div>

                <div className="mt-4 flex flex-wrap gap-3">
                  {creator.youtubeHandles?.map((youtube) => (
                    <CreatorSocialCard
                      url={`https://www.youtube.com/results?search_query=${youtube.handle}`}
                      platform="youtube"
                      handle={youtube.handle}
                      followers={youtube.followers}
                      posts={youtube.posts}
                    />
                  ))}
                  {creator.instagramHandles?.map((instagram) => (
                    <CreatorSocialCard
                      url={`https://www.instagram.com/${instagram.handle}`}
                      platform="instagram"
                      handle={instagram.handle}
                      followers={instagram.followers}
                      posts={instagram.posts}
                    />
                  ))}
                  {creator.tiktokHandles?.map((tiktok) => (
                    <CreatorSocialCard
                      url={`https://www.tiktok.com/@${tiktok.handle}`}
                      platform="tiktok"
                      handle={tiktok.handle}
                      followers={tiktok.followers}
                      posts={tiktok.posts}
                    />
                  ))}
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-primary/60">
                  <div className="flex items-center gap-1.5">
                    <Mail className="h-4 w-4" />
                    <span className="break-all">{creator.email}</span>
                  </div>
                  {isCreatorGridTeamMember && (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-primary/40">
                          UGC Accelerator Member
                        </span>
                        <Switch
                          checked={creator.isUGCAcceleratorMember ?? false}
                          onCheckedChange={async (checked) => {
                            try {
                              await updateUGCAcceleratorMember({
                                creatorId: creatorId as Id<"creators">,
                                isUGCAcceleratorMember: checked,
                              });
                              // Invalidate creator query to refetch updated data
                              await queryClient.invalidateQueries({
                                queryKey: [
                                  "query",
                                  api.creators.getCreatorByCreatorId,
                                  { creatorId: creatorId as Id<"creators"> },
                                ],
                              });
                              toast({
                                title: "Success",
                                description: `UGC Accelerator Member status ${checked ? "enabled" : "disabled"}`,
                              });
                            } catch (error) {
                              toast({
                                title: "Error",
                                description:
                                  error instanceof ConvexError
                                    ? error.data
                                    : "Failed to update status",
                                variant: "destructive",
                              });
                            }
                          }}
                          disabled={isUpdatingUGC}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-primary/40">
                          Creator Partner
                        </span>
                        <Switch
                          checked={
                            (
                              creator as {
                                isCreatorPartner?: boolean;
                              }
                            ).isCreatorPartner ?? false
                          }
                          onCheckedChange={async (checked) => {
                            try {
                              await updateIsCreatorPartner({
                                creatorId: creatorId as Id<"creators">,
                                isCreatorPartner: checked,
                              });
                              // Invalidate creator query to refetch updated data
                              await queryClient.invalidateQueries({
                                queryKey: [
                                  "query",
                                  api.creators.getCreatorByCreatorId,
                                  { creatorId: creatorId as Id<"creators"> },
                                ],
                              });
                              toast({
                                title: "Success",
                                description: `Creator Partner status ${checked ? "enabled" : "disabled"}`,
                              });
                            } catch (error) {
                              toast({
                                title: "Error",
                                description:
                                  error instanceof ConvexError
                                    ? error.data
                                    : "Failed to update status",
                                variant: "destructive",
                              });
                            }
                          }}
                          disabled={isUpdatingPartner}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-primary/40">
                          CiR (Creator In Residence)
                        </span>
                        <Switch
                          checked={
                            (
                              creator as {
                                isCiR?: boolean;
                              }
                            ).isCiR ?? false
                          }
                          onCheckedChange={async (checked) => {
                            try {
                              await updateIsCiR({
                                creatorId: creatorId as Id<"creators">,
                                isCiR: checked,
                              });
                              // Invalidate creator query to refetch updated data
                              await queryClient.invalidateQueries({
                                queryKey: [
                                  "query",
                                  api.creators.getCreatorByCreatorId,
                                  { creatorId: creatorId as Id<"creators"> },
                                ],
                              });
                              toast({
                                title: "Success",
                                description: `CiR status ${checked ? "enabled" : "disabled"}`,
                              });
                            } catch (error) {
                              toast({
                                title: "Error",
                                description:
                                  error instanceof ConvexError
                                    ? error.data
                                    : "Failed to update status",
                                variant: "destructive",
                              });
                            }
                          }}
                          disabled={isUpdatingCiR}
                        />
                      </div>
                      {customLeaderboard && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-primary/40">
                            Show Leaderboard on Home Screen
                          </span>
                          <Switch
                            checked={
                              (
                                creator as {
                                  showLeaderboardOnHomeScreen?: boolean;
                                }
                              ).showLeaderboardOnHomeScreen ?? false
                            }
                            onCheckedChange={async (checked) => {
                              try {
                                await updateShowLeaderboardOnHomeScreen({
                                  creatorId: creatorId as Id<"creators">,
                                  showLeaderboardOnHomeScreen: checked,
                                });
                                // Invalidate creator query to refetch updated data
                                await queryClient.invalidateQueries({
                                  queryKey: [
                                    "query",
                                    api.creators.getCreatorByCreatorId,
                                    { creatorId: creatorId as Id<"creators"> },
                                  ],
                                });
                                toast({
                                  title: "Success",
                                  description: `Leaderboard on home screen ${checked ? "enabled" : "disabled"}`,
                                });
                              } catch (error) {
                                toast({
                                  title: "Error",
                                  description:
                                    error instanceof ConvexError
                                      ? error.data
                                      : "Failed to update status",
                                  variant: "destructive",
                                });
                              }
                            }}
                            disabled={isUpdatingLeaderboard}
                          />
                        </div>
                      )}
                      {isCreatorGridTeamMember && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-primary/40">
                            Custom Leaderboard
                          </span>
                          <div className="flex items-center gap-2">
                            <Select
                              value={customLeaderboard?._id || "none"}
                              onValueChange={async (value) => {
                                if (value === "none") {
                                  // Remove from leaderboard
                                  try {
                                    await removeCreatorFromCustomLeaderboard({
                                      creatorId: creatorId as Id<"creators">,
                                    });
                                    await queryClient.invalidateQueries({
                                      queryKey: [
                                        "query",
                                        api.creators
                                          .getCreatorCustomLeaderboard,
                                        {
                                          creatorId:
                                            creatorId as Id<"creators">,
                                        },
                                      ],
                                    });
                                    await queryClient.invalidateQueries({
                                      queryKey: [
                                        "query",
                                        api.creators.getCreatorByCreatorId,
                                        {
                                          creatorId:
                                            creatorId as Id<"creators">,
                                        },
                                      ],
                                    });
                                    toast({
                                      title: "Success",
                                      description:
                                        "Creator removed from leaderboard",
                                    });
                                  } catch (error) {
                                    toast({
                                      title: "Error",
                                      description:
                                        error instanceof ConvexError
                                          ? error.data
                                          : "Failed to remove creator",
                                      variant: "destructive",
                                    });
                                  }
                                } else if (value === "create") {
                                  setShowCreateLeaderboardDialog(true);
                                } else {
                                  // Add to selected leaderboard
                                  try {
                                    await addCreatorToCustomLeaderboard({
                                      creatorId: creatorId as Id<"creators">,
                                      leaderboardId:
                                        value as Id<"customLeaderboards">,
                                    });
                                    await queryClient.invalidateQueries({
                                      queryKey: [
                                        "query",
                                        api.creators
                                          .getCreatorCustomLeaderboard,
                                        {
                                          creatorId:
                                            creatorId as Id<"creators">,
                                        },
                                      ],
                                    });
                                    await queryClient.invalidateQueries({
                                      queryKey: [
                                        "query",
                                        api.creators.getCreatorByCreatorId,
                                        {
                                          creatorId:
                                            creatorId as Id<"creators">,
                                        },
                                      ],
                                    });
                                    toast({
                                      title: "Success",
                                      description:
                                        "Creator added to leaderboard",
                                    });
                                  } catch (error) {
                                    toast({
                                      title: "Error",
                                      description:
                                        error instanceof ConvexError
                                          ? error.data
                                          : "Failed to add creator",
                                      variant: "destructive",
                                    });
                                  }
                                }
                              }}
                            >
                              <SelectTrigger className="h-8 w-[200px]">
                                <SelectValue
                                  placeholder={
                                    customLeaderboard
                                      ? customLeaderboard.name
                                      : "None"
                                  }
                                />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                {allCustomLeaderboards?.map((leaderboard) => (
                                  <SelectItem
                                    key={leaderboard._id}
                                    value={leaderboard._id}
                                  >
                                    {leaderboard.name}
                                  </SelectItem>
                                ))}
                                <SelectItem value="create">
                                  <div className="flex items-center gap-2">
                                    <Plus className="h-4 w-4" />
                                    Create New
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            {customLeaderboard && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={async () => {
                                  try {
                                    await removeCreatorFromCustomLeaderboard({
                                      creatorId: creatorId as Id<"creators">,
                                    });
                                    await queryClient.invalidateQueries({
                                      queryKey: [
                                        "query",
                                        api.creators
                                          .getCreatorCustomLeaderboard,
                                        {
                                          creatorId:
                                            creatorId as Id<"creators">,
                                        },
                                      ],
                                    });
                                    await queryClient.invalidateQueries({
                                      queryKey: [
                                        "query",
                                        api.creators.getCreatorByCreatorId,
                                        {
                                          creatorId:
                                            creatorId as Id<"creators">,
                                        },
                                      ],
                                    });
                                    toast({
                                      title: "Success",
                                      description:
                                        "Creator removed from leaderboard",
                                    });
                                  } catch (error) {
                                    toast({
                                      title: "Error",
                                      description:
                                        error instanceof ConvexError
                                          ? error.data
                                          : "Failed to remove creator",
                                      variant: "destructive",
                                    });
                                  }
                                }}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {creator.phone && (
                    <div className="flex items-center gap-1.5">
                      <Phone className="h-4 w-4" />
                      <span>{creator.phone}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Show Invite Creator button if there are no applications or partnerships */}
        {isAbleToInviteCreator && (
          <div className="flex justify-center">
            <Button
              size="lg"
              onClick={() => setShowCampaignSelector(true)}
              className="w-full max-w-md"
            >
              Invite Creator
            </Button>
          </div>
        )}

        {/* Conditionally render partnerships or applications first based on relevance */}
        {shouldShowPartnershipsFirst ? (
          <>
            {/* Partnerships Section */}
            <div className="rounded-lg border border-border bg-card">
              <div className="border-b border-border p-6">
                <h2 className="text-xl font-medium text-primary">
                  Partnerships
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-border bg-secondary">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                        Campaign
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                        Start Date
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                        End Date
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {creatorContractsAndApplications?.creatorPartnerships
                      .length ? (
                      creatorContractsAndApplications.creatorPartnerships.map(
                        (partnership) => {
                          return (
                            <tr
                              key={partnership._id}
                              className="cursor-pointer hover:bg-secondary/50 transition-colors"
                            >
                              <Link
                                to="/partnerships/$partnershipId"
                                params={{ partnershipId: partnership._id }}
                                className="contents"
                              >
                                <td className="px-6 py-4 text-sm text-primary">
                                  {partnership.campaignName}
                                </td>
                                <td className="px-6 py-4 text-sm text-primary">
                                  {partnership.startDate
                                    ? formatDate(
                                        new Date(
                                          partnership.startDate,
                                        ).toISOString(),
                                      )
                                    : "-"}
                                </td>
                                <td className="px-6 py-4 text-sm text-primary">
                                  {partnership.endDate
                                    ? formatDate(
                                        new Date(
                                          partnership.endDate,
                                        ).toISOString(),
                                      )
                                    : "-"}
                                </td>
                                <td className="px-6 py-4 text-sm">
                                  <span
                                    className={cn(
                                      "font-medium",
                                      getStatusColor(partnership.status),
                                    )}
                                  >
                                    {getStatusDisplayText(partnership.status)}
                                  </span>
                                </td>
                              </Link>
                            </tr>
                          );
                        },
                      )
                    ) : (
                      <tr>
                        <td
                          colSpan={3}
                          className="px-6 py-4 text-sm text-primary/60 text-center"
                        >
                          No past partnerships found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Applications Section */}
            {creatorContractsAndApplications?.creatorApplications.map(
              (application) => {
                return (
                  <div
                    key={application._id}
                    className="rounded-lg border border-border bg-card mb-4"
                  >
                    <div className="border-b border-border p-6">
                      <div className="flex justify-between items-center">
                        <h2 className="text-xl font-medium text-primary">
                          Application for {application.campaignName}
                        </h2>
                        <span
                          className={cn(
                            "px-3 py-1 rounded-full text-sm font-medium",
                            application.status === "pending"
                              ? "bg-yellow-500/10 text-yellow-500"
                              : application.status === "approved"
                                ? "bg-green-500/10 text-green-500"
                                : "bg-red-500/10 text-red-500",
                          )}
                        >
                          {application.status.charAt(0).toUpperCase() +
                            application.status.slice(1)}
                        </span>
                      </div>
                      <p className="text-sm text-primary/60 mt-2">
                        Submitted on{" "}
                        {formatDate(
                          new Date(application._creationTime).toISOString(),
                        )}
                      </p>
                    </div>
                    <div className="p-6">
                      {application.message && (
                        <div className="mb-6">
                          <h3 className="mb-2 font-medium text-primary">
                            Message
                          </h3>
                          <p className="text-primary/60">
                            {application.message}
                          </p>
                        </div>
                      )}

                      <div className="mb-6">
                        <h3 className="mb-2 font-medium text-primary">
                          Application Videos
                        </h3>
                        <ul className="list-inside list-disc space-y-2">
                          {application.links?.map((link, i) => (
                            <li key={i}>
                              <ApplicationVideoLink
                                storageId={link as Id<"_storage"> | string}
                                index={i}
                              />
                            </li>
                          ))}
                        </ul>
                      </div>

                      {application.status === "pending" && (
                        <div className="flex gap-4">
                          <Button
                            onClick={() =>
                              handleApplicationAction(
                                "approve",
                                application._id,
                                application.campaignId as Id<"campaigns">,
                              )
                            }
                            disabled={showContractForm}
                          >
                            Set Terms
                          </Button>
                          <Button
                            onClick={() =>
                              handleApplicationAction(
                                "reject",
                                application._id,
                                application.campaignId as Id<"campaigns">,
                              )
                            }
                            variant="destructive"
                            disabled={showContractForm}
                          >
                            Reject Application
                          </Button>
                        </div>
                      )}

                      {application.status === "rejected" &&
                        application.rejectionReason && (
                          <div className="mt-4 p-4 rounded-lg bg-red-500/10">
                            <h3 className="font-medium text-primary mb-2">
                              Rejection Reason
                            </h3>
                            <p className="text-primary/60">
                              {application.rejectionReason}
                            </p>
                          </div>
                        )}
                    </div>
                  </div>
                );
              },
            )}
          </>
        ) : (
          <>
            {/* Applications Section (shown first when there's a pending application and no active partnership) */}
            {creatorContractsAndApplications?.creatorApplications.map(
              (application) => {
                return (
                  <div
                    key={application._id}
                    className="rounded-lg border border-border bg-card mb-4"
                  >
                    <div className="border-b border-border p-6">
                      <div className="flex justify-between items-center">
                        <h2 className="text-xl font-medium text-primary">
                          Application for {application.campaignName}
                        </h2>
                        <span
                          className={cn(
                            "px-3 py-1 rounded-full text-sm font-medium",
                            application.status === "pending"
                              ? "bg-yellow-500/10 text-yellow-500"
                              : application.status === "approved"
                                ? "bg-green-500/10 text-green-500"
                                : "bg-red-500/10 text-red-500",
                          )}
                        >
                          {application.status.charAt(0).toUpperCase() +
                            application.status.slice(1)}
                        </span>
                      </div>
                      <p className="text-sm text-primary/60 mt-2">
                        Submitted on{" "}
                        {formatDate(
                          new Date(application._creationTime).toISOString(),
                        )}
                      </p>
                    </div>
                    <div className="p-6">
                      {application.message && (
                        <div className="mb-6">
                          <h3 className="mb-2 font-medium text-primary">
                            Message
                          </h3>
                          <p className="text-primary/60">
                            {application.message}
                          </p>
                        </div>
                      )}

                      <div className="mb-6">
                        <h3 className="mb-2 font-medium text-primary">
                          Application Videos
                        </h3>
                        <ul className="list-inside list-disc space-y-2">
                          {application.links?.map((link, i) => (
                            <li key={i}>
                              <ApplicationVideoLink
                                storageId={link as Id<"_storage"> | string}
                                index={i}
                              />
                            </li>
                          ))}
                        </ul>
                      </div>

                      {application.status === "pending" && (
                        <div className="flex gap-4">
                          <Button
                            onClick={() =>
                              handleApplicationAction(
                                "approve",
                                application._id,
                                application.campaignId as Id<"campaigns">,
                              )
                            }
                            disabled={showContractForm}
                          >
                            Set Terms
                          </Button>
                          <Button
                            onClick={() =>
                              handleApplicationAction(
                                "reject",
                                application._id,
                                application.campaignId as Id<"campaigns">,
                              )
                            }
                            variant="destructive"
                            disabled={showContractForm}
                          >
                            Reject Application
                          </Button>
                        </div>
                      )}

                      {application.status === "rejected" &&
                        application.rejectionReason && (
                          <div className="mt-4 p-4 rounded-lg bg-red-500/10">
                            <h3 className="font-medium text-primary mb-2">
                              Rejection Reason
                            </h3>
                            <p className="text-primary/60">
                              {application.rejectionReason}
                            </p>
                          </div>
                        )}
                    </div>
                  </div>
                );
              },
            )}

            {/* Partnerships Section */}
            <div className="rounded-lg border border-border bg-card">
              <div className="border-b border-border p-6">
                <h2 className="text-xl font-medium text-primary">
                  Partnerships
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-border bg-secondary">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                        Campaign
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                        Start Date
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                        End Date
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {creatorContractsAndApplications?.creatorPartnerships
                      .length ? (
                      creatorContractsAndApplications.creatorPartnerships.map(
                        (partnership) => {
                          return (
                            <tr
                              key={partnership._id}
                              className="cursor-pointer hover:bg-secondary/50 transition-colors"
                            >
                              <Link
                                to="/partnerships/$partnershipId"
                                params={{ partnershipId: partnership._id }}
                                className="contents"
                              >
                                <td className="px-6 py-4 text-sm text-primary">
                                  {partnership.campaignName}
                                </td>
                                <td className="px-6 py-4 text-sm text-primary">
                                  {partnership.startDate
                                    ? formatDate(
                                        new Date(
                                          partnership.startDate,
                                        ).toISOString(),
                                      )
                                    : "-"}
                                </td>
                                <td className="px-6 py-4 text-sm text-primary">
                                  {partnership.endDate
                                    ? formatDate(
                                        new Date(
                                          partnership.endDate,
                                        ).toISOString(),
                                      )
                                    : "-"}
                                </td>
                                <td className="px-6 py-4 text-sm">
                                  <span
                                    className={cn(
                                      "font-medium",
                                      getStatusColor(partnership.status),
                                    )}
                                  >
                                    {getStatusDisplayText(partnership.status)}
                                  </span>
                                </td>
                              </Link>
                            </tr>
                          );
                        },
                      )
                    ) : (
                      <tr>
                        <td
                          colSpan={3}
                          className="px-6 py-4 text-sm text-primary/60 text-center"
                        >
                          No past partnerships found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Campaign Selector Dialog for Direct Invites */}
        <Dialog
          open={showCampaignSelector}
          onOpenChange={setShowCampaignSelector}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Select Campaign</DialogTitle>
              <DialogDescription>
                Choose which campaign to invite this creator to.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="space-y-2">
                {creatorContractsAndApplications?.availableCampaigns?.map(
                  (campaign) => (
                    <Button
                      key={campaign._id}
                      variant="outline"
                      className="w-full justify-start text-left"
                      onClick={() => {
                        setSelectedCampaignId(campaign._id);
                        setShowCampaignSelector(false);
                        setShowContractForm(true);
                      }}
                    >
                      <div>
                        <div className="font-medium">{campaign.name}</div>
                        {campaign.endDate && (
                          <div className="text-xs text-muted-foreground">
                            Ends:{" "}
                            {new Date(campaign.endDate).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </Button>
                  ),
                )}
                {(!creatorContractsAndApplications?.availableCampaigns ||
                  creatorContractsAndApplications.availableCampaigns.length ===
                    0) && (
                  <p className="text-center text-muted-foreground py-4">
                    No active campaigns found
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowCampaignSelector(false)}
              >
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Contract Form Dialog */}
        <PartnershipTermsDialog
          open={showContractForm}
          onOpenChange={setShowContractForm}
          contractDetails={contractDetails}
          onContractDetailsChange={setContractDetails}
          onSubmit={handleContractSubmit}
          isSubmitting={isCreatingPartnership}
          maxEndDate={selectedCampaign?.endDate}
        />

        {/* Rejection Dialog */}
        <Dialog
          open={showRejectionDialog}
          onOpenChange={setShowRejectionDialog}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Application</DialogTitle>
              <DialogDescription>
                Please provide a reason for rejecting this application. This
                will be shared with the creator.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Textarea
                placeholder="Enter rejection reason..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="min-h-[100px]"
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowRejectionDialog(false);
                  setRejectionReason("");
                  setSelectedApplicationId(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleRejectConfirm}
                disabled={!rejectionReason.trim()}
              >
                Confirm Rejection
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Custom Leaderboard Dialog */}
        <Dialog
          open={showCreateLeaderboardDialog}
          onOpenChange={setShowCreateLeaderboardDialog}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Custom Leaderboard</DialogTitle>
              <DialogDescription>
                Enter a name for the new custom leaderboard.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Input
                placeholder="Leaderboard name"
                value={newLeaderboardName}
                onChange={(e) => setNewLeaderboardName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newLeaderboardName.trim()) {
                    handleCreateLeaderboard();
                  }
                }}
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateLeaderboardDialog(false);
                  setNewLeaderboardName("");
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateLeaderboard}
                disabled={!newLeaderboardName.trim()}
              >
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );

  async function handleCreateLeaderboard() {
    if (!newLeaderboardName.trim()) return;

    try {
      const { leaderboardId } = await createCustomLeaderboard({
        name: newLeaderboardName.trim(),
      });
      // Add creator to the newly created leaderboard
      await addCreatorToCustomLeaderboard({
        creatorId: creatorId as Id<"creators">,
        leaderboardId: leaderboardId,
      });
      await queryClient.invalidateQueries({
        queryKey: ["query", api.creators.getAllCustomLeaderboards, {}],
      });
      await queryClient.invalidateQueries({
        queryKey: [
          "query",
          api.creators.getCreatorCustomLeaderboard,
          { creatorId: creatorId as Id<"creators"> },
        ],
      });
      await queryClient.invalidateQueries({
        queryKey: [
          "query",
          api.creators.getCreatorByCreatorId,
          { creatorId: creatorId as Id<"creators"> },
        ],
      });
      setShowCreateLeaderboardDialog(false);
      setNewLeaderboardName("");
      toast({
        title: "Success",
        description: "Custom leaderboard created and creator added",
      });
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof ConvexError
            ? error.data
            : "Failed to create leaderboard",
        variant: "destructive",
      });
    }
  }
}
